import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { ALERT_CENTER_NS } from 'src/alerts/AlertCenter.constants';
import {
  hasMetaPricingFavor,
  hasMetaPricingTools,
  hasMetaPricingWave,
  META_PRICING_BALANCE_COPY_KEYS,
  META_PRICING_CLOSING_KEYS,
  META_PRICING_CTA_LABEL_KEYS,
  META_PRICING_PARAGRAPH_KEYS,
  META_PRICING_TIPS_COPY_KEYS,
  META_PRICING_TODAY_LABEL_KEYS,
  META_PRICING_VIDEO_COPY_PREFIXES,
  resolveMetaPricingAmounts,
  resolveMetaPricingVideo,
} from 'src/alerts/MetaPricingNotice.functions';
import { getMetaPricingMessage } from 'src/alerts/MetaPricingNotice.types';
import {
  ALERT_CENTER_EVENTS,
  type tAlertDetailDismissedForeverProps,
  type tAlertDetailVideoPlayedProps,
  type tMetaPricingEventProps,
} from 'src/hooks/analytics/alertCenterAnalytics.constants';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useAlertCenterContext } from 'src/providers/AlertCenterProvider/AlertCenter.hook';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useAlertCtaAnalytics } from '../AlertCenter.exposure.hook';
import {
  isMetaPricingNoticeDismissedForever,
  markMetaPricingNoticeSeenThisSession,
  setMetaPricingNoticeDismissedForever,
} from '../MetaPricingNotice.storage';
import type { tAlertDetailRendererProps } from './AlertDetail.registry';
import MetaPricingVideo, {
  type tMetaPricingVideoPlayer,
} from './MetaPricingVideo.component';
import './MetaPricingDetail.styles.scss';

/** Below this many pixels of unread body, the fade over the footer is noise. */
const SCROLL_FADE_THRESHOLD = 8;

/**
 * AGI-428: the Meta-pricing notice's detail, and the modal the owner meets on
 * the way in. Same component on both paths (automatic host and bell row), so
 * the two can never drift into two different explanations of the same charge.
 *
 * Built from the approved prototype
 * (`docs/prototypes/2026-09-15-aviso-precios-meta/template.html`), with three
 * deliberate differences from it, each because the host modal already owns the
 * thing:
 *
 *  1. THE HEADLINE LIVES IN THE MODAL HEADER. The prototype's eyebrow plus
 *     "¡Hola, X!" title become the body's first line here, and the modal keeps
 *     `metaPricing.row.title` in its header. Every other alert detail reads
 *     that way, and a dialog whose header greets you while the bell row it
 *     came from announces a price change is two different documents. Since
 *     Ariel's staging review (16-sep) that header is title and close only,
 *     with no tier icon (`ALERT_DETAIL_PLAIN_HEADER_IDS`).
 *  2. THE BODY IS A CAPPED SCROLL AREA, not the dialog's own scroll with a
 *     sticky footer. `AlertDetailModal__Dialog` is the scroll container for
 *     every renderer; taking it over from inside one renderer would change how
 *     the other six behave. The effect Ariel asked for is preserved exactly:
 *     the actions stay visible without scrolling on a small screen, and the
 *     body fades into them while there is more to read.
 *  3. THE FOOTER IS THIS COMPONENT'S. By the footer contract in
 *     `AlertDetail.registry.ts`, a `detail` CTA means the renderer paints its
 *     own actions, so the modal adds no button of its own. That is also what
 *     keeps the "No volver a mostrar" checkbox out of reach of the bell's
 *     acknowledgment, which it must never touch.
 *
 * MESSAGE 2 (25-sep) IS THIS SAME COMPONENT. The notice key says which
 * message is on screen (`getMetaPricingMessage`): message 2 swaps the copy
 * tables, drops the green block and the tools, adds the video player after
 * the amounts card, and keeps its own storage marks. The dialog, the card and
 * the footer are the same on purpose ("con el mismo diseno anterior").
 *
 * SO IS MESSAGE 3 (6-oct, the new balance). It has no amounts: its card is
 * the free window for Click to WhatsApp ads (72 hours before, 7 days now),
 * built from the same card so the three messages read as one series, and
 * Dany's video follows it. Same footer, same CTA per urgency, its own marks.
 *
 * 🔴 THE CHECKBOX WRITES ON TOGGLE, NOT ON CLOSE. The prototype defers the
 * write to `closeModal`, which it can because it owns every exit. Here Escape
 * and a backdrop click reach `AlertDetailModal.requestClose` directly and this
 * component never hears about them, so a deferred write would silently lose
 * the preference on two of the three ways out. Unticking removes the mark for
 * the same reason.
 */
export default function MetaPricingDetail(props: tAlertDetailRendererProps) {
  const { t } = useTranslation(ALERT_CENTER_NS);
  const { metaPricingNotice } = useAlertCenterContext();
  const { currentUser, currentUserId } = useUserContext();
  const { currentWorkspace, currentWorkspaceId } = useWorkspaceContext();
  const { trackEvent } = useAnalytics();
  const trackCtaClick = useAlertCtaAnalytics('detail');

  const bodyRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasMore, setHasMore] = useState(false);

  const payload = metaPricingNotice?.payload ?? null;
  const message = metaPricingNotice
    ? getMetaPricingMessage(metaPricingNotice.key)
    : 'impact';
  const workspaceId = currentWorkspaceId ?? '';
  const userId = currentUserId ?? '';
  const hasScope = workspaceId.length > 0 && userId.length > 0;

  // Lazy initializer, safe because the host mounts this tree only once both
  // ids exist and with `key={workspaceId + userId}`: a late-resolving id can
  // never leave this reading (or later clobbering) the wrong user's mark.
  const [isDismissedForever, setIsDismissedForever] = useState(() =>
    hasScope
      ? isMetaPricingNoticeDismissedForever({ workspaceId, userId, message })
      : false,
  );

  /*
   * 🔴 RE-READ THE MARK WHEN THE SCOPE CHANGES UNDER AN OPEN MODAL (CRisPR,
   * #3437). The notice can switch from message 1 to message 2 on a focus
   * refetch while this dialog is open; the checkbox would otherwise keep
   * message 1's answer while its writes already go to message 2's key.
   * Adjusted during render (React's "reset state when a prop changes"
   * pattern), not in an effect, so no frame paints the stale answer.
   */
  const scopeKey = `${workspaceId}:${userId}:${message}`;
  const [readScopeKey, setReadScopeKey] = useState(scopeKey);
  if (readScopeKey !== scopeKey) {
    setReadScopeKey(scopeKey);
    setIsDismissedForever(
      hasScope
        ? isMetaPricingNoticeDismissedForever({ workspaceId, userId, message })
        : false,
    );
  }

  /*
   * Seen THIS SESSION, written by whichever path rendered the detail. Doing it
   * here rather than only in the automatic host is what makes "opened it from
   * the bell" count: the host would otherwise re-open the modal later in the
   * same session for someone who has already read it.
   */
  useEffect(() => {
    if (!workspaceId || !userId) return;
    markMetaPricingNoticeSeenThisSession({ workspaceId, userId, message });
  }, [workspaceId, userId, message]);

  const updateHasMore = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    setHasMore(
      body.scrollHeight - body.clientHeight - body.scrollTop >
        SCROLL_FADE_THRESHOLD,
    );
  }, []);

  /*
   * Layout effect, not a plain one: the fade says whether the footer is
   * covering something, and computing it after paint shows a frame with the
   * wrong answer on every open.
   *
   * 🔴 AND A SECOND READ ONE FRAME LATER, which is the prototype's
   * `requestAnimationFrame(updateMore)` and not belt-and-braces. Measured in
   * the headless capture: on the first layout pass the body reported
   * `scrollHeight === clientHeight` (the cap had not been resolved against the
   * viewport yet), so the fade stayed off over a body that really did have
   * 130px left to read, and nothing re-ran it until a window resize that never
   * comes.
   */
  /*
   * 🔴 A ResizeObserver ON THE CONTENT, not a timer and not the fonts promise.
   *
   * Measured in the headless capture: 200ms after load the body reported
   * `scrollHeight === clientHeight` (463/463) and at 300ms it was 600/468, so
   * every one-shot re-measure (mount, one animation frame, `fonts.ready`)
   * answered "nothing left to read" over a body with 130px still to go, and
   * only a window resize that never comes would have corrected it. The fade
   * exists precisely to tell someone there IS more, so measuring it once and
   * hoping is the one thing it cannot do.
   *
   * The observer watches the CONTENT wrapper rather than the scroll box: the
   * box is capped at 52vh and stops changing, while the content is what grows
   * as text reflows. `scroll` stays as the running update.
   */
  useLayoutEffect(() => {
    updateHasMore();
    const content = contentRef.current;
    // jsdom stubs ResizeObserver as a no-op (src/test/setup.ts), which is
    // exactly right for unit tests: nothing there has a layout to observe.
    const observer =
      content && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => updateHasMore())
        : null;
    observer?.observe(content as Element);
    window.addEventListener('resize', updateHasMore);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateHasMore);
    };
  }, [updateHasMore, payload]);

  if (!payload) {
    // The alert cannot be live without a payload, so this is the race where
    // the row expired under an open modal. Say so plainly instead of painting
    // a card with no numbers in it.
    return (
      <p className="MetaPricingDetail__Resolved" role="status">
        <Trans t={t} i18nKey="detail.resolved" />
      </p>
    );
  }

  const noticeProps: tMetaPricingEventProps = {
    segment: payload.segment,
    urgency: payload.urgency,
    key_account: payload.keyAccount,
    cta_url: payload.ctaUrl,
  };

  const greetingName =
    currentUser?.firstName?.trim() ||
    currentWorkspace?.name?.trim() ||
    payload.businessName;
  const amounts = resolveMetaPricingAmounts(payload);
  const tipsCopy =
    message === 'tips'
      ? META_PRICING_TIPS_COPY_KEYS[payload.copyVariant]
      : null;
  const balanceCopy =
    message === 'balance'
      ? META_PRICING_BALANCE_COPY_KEYS[payload.copyVariant]
      : null;
  // Messages 2 and 3 share the shape around the video: lead, card, intro,
  // player, question, sign-off. Message 1 keeps its own tables.
  const videoCopy = balanceCopy ?? tipsCopy;
  const paragraphKeys = videoCopy
    ? videoCopy.lead
    : META_PRICING_PARAGRAPH_KEYS[payload.copyVariant];
  const closing: { question?: string; sign?: string } = videoCopy
    ? { question: videoCopy.question, sign: videoCopy.sign }
    : META_PRICING_CLOSING_KEYS[payload.copyVariant];
  // Messages 2 and 3 carry the video instead: no green block, no tools block.
  const showFavor =
    message === 'impact' && hasMetaPricingFavor(payload.copyVariant);
  const showTools =
    message === 'impact' && hasMetaPricingTools(payload.urgency);
  // Without a playable URL the intro would announce a video that is not
  // there, so the whole block waits for the data update that sets it.
  const video = videoCopy ? resolveMetaPricingVideo(payload.videoUrl) : null;
  const ctaLabelKey = META_PRICING_CTA_LABEL_KEYS[payload.urgency];
  // An empty `cta_url` is a link still pending, not a defect: render no button
  // at all rather than one that goes nowhere, and let the link arrive by a
  // data update. With a link the button is always there: every CTA works at
  // any hour (AGI-428, Ariel, 18-sep), so there is no opening-hours state.
  const hasCta = payload.ctaUrl.trim().length > 0;

  const onToggleDismissForever = (checked: boolean) => {
    setIsDismissedForever(checked);
    if (hasScope) {
      setMetaPricingNoticeDismissedForever({
        workspaceId,
        userId,
        message,
        dismissed: checked,
      });
    }
    if (!checked) return;
    const dismissProps: tAlertDetailDismissedForeverProps = {
      alert_id: props.alert.id,
      ...noticeProps,
    };
    trackEvent(ALERT_CENTER_EVENTS.DETAIL_DISMISSED_FOREVER, dismissProps);
  };

  const onPlayVideo = (player: tMetaPricingVideoPlayer) => {
    const videoProps: tAlertDetailVideoPlayedProps = {
      alert_id: props.alert.id,
      player,
      ...noticeProps,
    };
    trackEvent(ALERT_CENTER_EVENTS.DETAIL_VIDEO_PLAYED, videoProps);
  };

  return (
    <div className="MetaPricingDetail" data-testid="meta-pricing-detail">
      <div
        ref={bodyRef}
        className="MetaPricingDetail__Body"
        onScroll={updateHasMore}
      >
        <div ref={contentRef} className="MetaPricingDetail__Content">
          <p className="MetaPricingDetail__Greeting">
            {t(
              message === 'tips'
                ? 'metaPricing.tips.greeting'
                : 'metaPricing.greeting',
              {
                name: greetingName,
              },
            )}
            {hasMetaPricingWave(payload.copyVariant) ? (
              <span aria-hidden="true"> 👋</span>
            ) : null}
          </p>

          {paragraphKeys.map((key) => (
            <p key={key} className="MetaPricingDetail__Paragraph">
              {/*
              `<0>` is the emphasis every Alert Center body already uses; `<1>`
              is the sentence that attributes the change to Meta, in the error
              text colour and bold. Daniela's note, 15-sep: "nadie lee", so the
              one line that says this is not Leadsales' decision has to be the
              one line that cannot be skimmed past.
            */}
              <Trans
                t={t}
                i18nKey={key}
                values={{ ws: payload.businessName }}
                components={[
                  <span key="0" className="font-semibold" />,
                  <span key="1" className="MetaPricingDetail__Meta" />,
                ]}
              />
            </p>
          ))}

          {message === 'balance' ? (
            <div
              className="MetaPricingDetail__Amounts"
              data-testid="meta-pricing-window"
            >
              {/*
              Message 3's card: the free window for Click to WhatsApp ads, in
              the same rows the estimate used, so "Ahora" carries the tint the
              October figure had. The note under it is the variant's own line
              (absent for low urgency, as in Growth's mockup).
            */}
              <p className="MetaPricingDetail__AmountsTitle">
                {t('metaPricing.balance.window.title')}
              </p>
              <div className="MetaPricingDetail__AmountsRow">
                <span className="MetaPricingDetail__AmountsLabel">
                  {t('metaPricing.balance.window.before')}
                </span>
                <span className="MetaPricingDetail__AmountsValue">
                  {t('metaPricing.balance.window.beforeValue')}
                </span>
              </div>
              <div className="MetaPricingDetail__AmountsRow MetaPricingDetail__AmountsRow--estimate">
                <span className="MetaPricingDetail__AmountsLabel">
                  {t('metaPricing.balance.window.now')}
                </span>
                <span
                  className="MetaPricingDetail__AmountsValue"
                  data-testid="meta-pricing-window-now"
                >
                  {t('metaPricing.balance.window.nowValue')}
                </span>
              </div>
              {balanceCopy?.windowNote ? (
                <p
                  className="MetaPricingDetail__AmountsNote MetaPricingDetail__AmountsNote--emphasis"
                  data-testid="meta-pricing-window-note"
                >
                  {t(balanceCopy.windowNote)}
                </p>
              ) : null}
            </div>
          ) : (
            <div className="MetaPricingDetail__Amounts">
              <div className="MetaPricingDetail__AmountsRow">
                <span className="MetaPricingDetail__AmountsLabel">
                  {amounts.today === null
                    ? t('metaPricing.amounts.noChargeToday')
                    : t(META_PRICING_TODAY_LABEL_KEYS[message])}
                </span>
                <span
                  className="MetaPricingDetail__AmountsValue"
                  data-testid="meta-pricing-amount-today"
                >
                  {amounts.today === null ? (
                    <small>$0</small>
                  ) : (
                    <>
                      {amounts.today}
                      <small>
                        {amounts.currency}
                        {t('metaPricing.amounts.perMonth')}
                      </small>
                    </>
                  )}
                </span>
              </div>
              <div className="MetaPricingDetail__AmountsRow MetaPricingDetail__AmountsRow--estimate">
                <span className="MetaPricingDetail__AmountsLabel">
                  {t('metaPricing.amounts.october')}
                </span>
                <span
                  className="MetaPricingDetail__AmountsValue"
                  data-testid="meta-pricing-amount-october"
                >
                  {amounts.october}
                  <small>
                    {amounts.currency}
                    {t('metaPricing.amounts.perMonth')}
                  </small>
                </span>
              </div>
              <p
                className="MetaPricingDetail__AmountsNote"
                data-testid="meta-pricing-amounts-note"
              >
                {/*
              The MXN sentence rides with the CURRENCY, not with the copy
              variant: it is a fact about the conversion (USD x 21 plus IVA,
              the wallet rule), so every MXN workspace needs it and no USD
              workspace does.
            */}
                {amounts.isApproximate
                  ? `${t('metaPricing.amounts.mxnNote')} ${t('metaPricing.amounts.estimateNote')}`
                  : t('metaPricing.amounts.estimateNote')}
              </p>
            </div>
          )}

          {showFavor ? (
            <div
              className="MetaPricingDetail__Favor"
              role="note"
              data-testid="meta-pricing-favor"
            >
              <span className="MetaPricingDetail__FavorIcon" aria-hidden="true">
                🚀
              </span>
              <div>
                <p className="MetaPricingDetail__FavorLine">
                  <Trans
                    t={t}
                    i18nKey="metaPricing.favor.ads"
                    components={[<span key="0" className="font-semibold" />]}
                  />
                </p>
                <p className="MetaPricingDetail__FavorLine">
                  {t('metaPricing.favor.freeTier')}
                </p>
              </div>
            </div>
          ) : null}

          {showTools ? (
            <div
              className="MetaPricingDetail__Tools"
              data-testid="meta-pricing-tools"
            >
              <p className="MetaPricingDetail__Paragraph">
                {t('metaPricing.tools.intro')}
              </p>
              {payload.videoUrl ? (
                <a
                  className="MetaPricingDetail__VideoCard"
                  href={payload.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="meta-pricing-video"
                >
                  <span
                    className="MetaPricingDetail__VideoPlay"
                    aria-hidden="true"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                  <span>
                    <span className="MetaPricingDetail__VideoTitle">
                      {t('metaPricing.tools.videoTitle')}
                    </span>
                    <span className="MetaPricingDetail__VideoLink">
                      {t('metaPricing.tools.videoLink')}
                    </span>
                  </span>
                </a>
              ) : null}
              <div>
                <p className="MetaPricingDetail__TipsTitle">
                  <Trans
                    t={t}
                    i18nKey="metaPricing.tools.tipsTitle"
                    components={[<span key="0" className="font-semibold" />]}
                  />
                </p>
                <ul className="MetaPricingDetail__Tips">
                  <li>{t('metaPricing.tools.tip1')}</li>
                  <li>{t('metaPricing.tools.tip2')}</li>
                  <li>{t('metaPricing.tools.tip3')}</li>
                </ul>
              </div>
            </div>
          ) : null}

          {videoCopy && video && message !== 'impact' ? (
            <div
              className="MetaPricingDetail__Tools"
              data-testid="meta-pricing-tips-video"
            >
              <p className="MetaPricingDetail__Paragraph">
                {t(videoCopy.videoIntro)}
              </p>
              <MetaPricingVideo
                video={video}
                onPlay={onPlayVideo}
                copyPrefix={META_PRICING_VIDEO_COPY_PREFIXES[message]}
              />
            </div>
          ) : null}

          {closing.question ? (
            <p className="MetaPricingDetail__Paragraph">
              {t(closing.question)}
            </p>
          ) : null}
          {closing.sign ? (
            <p className="MetaPricingDetail__Paragraph">{t(closing.sign)}</p>
          ) : null}
        </div>
      </div>

      <div
        className={classNames('MetaPricingDetail__Footer', {
          'MetaPricingDetail__Footer--hasMore': hasMore,
        })}
        data-has-more={hasMore || undefined}
      >
        <label className="MetaPricingDetail__Checkbox">
          <input
            type="checkbox"
            checked={isDismissedForever}
            data-testid="meta-pricing-never-again"
            onChange={(event) => onToggleDismissForever(event.target.checked)}
          />
          <span>{t('metaPricing.footer.neverAgain')}</span>
        </label>
        <div className="MetaPricingDetail__Actions">
          <button
            type="button"
            className="MetaPricingDetail__Close"
            data-testid="meta-pricing-close"
            onClick={props.onRequestClose}
          >
            {t('metaPricing.footer.close')}
          </button>
          {hasCta ? (
            <a
              className="MetaPricingDetail__Cta"
              href={payload.ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="meta-pricing-cta"
              onClick={() =>
                // Through the shared emitter so this click lands in the same
                // funnel as every other Alert Center CTA. `cta_kind` is
                // overridden because the DECLARATION says `detail` (which is
                // what opened this dialog) while the button the user actually
                // pressed is an external link.
                trackCtaClick(props.alert, {
                  cta_kind: 'external',
                  ...noticeProps,
                })
              }
            >
              {t(ctaLabelKey)}
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}
